// Design A ("Corporate Band") visual language for Manufacturing vouchers --
// Print Preview only, used when institution_profile.print_template is
// 'design_a'. Mirrors backend/src/pdfTemplates/designA/manufacturingAdapter.js
// exactly: a BESPOKE layout, not the shared VoucherBodyDesignA component,
// because Manufacturing has no customer/Bill-To party, no GST/VAT, and two
// independent item tables (produced vs. consumed) instead of one -- see that
// adapter file's header comment for the full reasoning, including the open
// product question of whether this voucher type should even join Design A.
// ManufacturingVoucherBody.jsx (legacy) is untouched.
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { formatToCompanyTimezone } from "../../../../lib/functions";
import { numberFormat } from "../../../../lib/functions";
import { convertAmountToWordsDesignA } from "../../../../lib/designAWords";
import { fitItemName } from "../../../../lib/designAItemFit";
import { manufacturingRecordWithDetailseApi } from "../../../../apiservice/manufacturing";
import { getInstitutionApi } from "../../../../apiservice/setting";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import "../../../../components/common/VoucherBodyDesignA.css";

function ItemNameCell({ name }) {
  const fit = fitItemName(name, { maxChars: 38 });
  return <span style={{ fontSize: `${fit.fontSizePercent}%` }}>{fit.text}</span>;
}

function ItemsTable({ title, rows }) {
  return (
    <>
      <p className="da-label" style={{ marginTop: 16 }}>{title}</p>
      <table className="da-table">
        <thead>
          <tr>
            <th>#</th><th>Item</th><th style={{ textAlign: "right" }}>Qty</th>
            <th style={{ textAlign: "right" }}>Rate</th><th style={{ textAlign: "right" }}>Per</th><th style={{ textAlign: "right" }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td className="da-col-item"><ItemNameCell name={r.item_name} /></td>
              <td className="da-num-cell">{r.qty}</td>
              <td className="da-num-cell">{r.rate}</td>
              <td className="da-num-cell">{r.unit}</td>
              <td className="da-amount-cell">{r.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

export default function ManufacturingVoucherBodyDesignA({ mf_id }) {
  const [data, setData] = useState(null);
  const [institution, setInstitution] = useState(null);
  const authInfo = useSelector(selectAuthInfo);

  useEffect(() => {
    if (!mf_id) return;
    manufacturingRecordWithDetailseApi({ mf_id, from: "voucher" }).then((res) => {
      if (res?.data?.length) setData(res.data[0]);
    });
  }, [mf_id]);

  useEffect(() => {
    getInstitutionApi().then((res) => setInstitution(res?.data || null)).catch(() => {});
  }, []);

  if (!data) return null;

  const currency = authInfo?.currency || institution?.currency;
  const country = authInfo?.country || institution?.country || "";
  const isUAE = country.trim().toLowerCase() === "united arab emirates";
  const subscriptionType = (authInfo?.subscription_type || "").toLowerCase();
  const isPharma = isUAE && (subscriptionType.includes("pharma") || subscriptionType.includes("pharmabooks"));

  const useBranch = !!authInfo?.branch_info;
  const logoUrl = useBranch ? institution?.branch_logo : institution?.logo;
  const companyName = (useBranch ? institution?.branch_name : institution?.name) || "Institution";

  const dateOnly = (formatToCompanyTimezone(data.created_date) || "").split(" ")[0];
  const dateReversed = dateOnly ? dateOnly.split("-").reverse().join("-") : "";

  const finishedRows = (data.miDetails || []).map((d) => ({
    item_name: d.item_name, qty: d.pd_qty, rate: numberFormat(d.item_rate), unit: d.unit_name, total: numberFormat(d.item_total),
  }));
  const rawRows = (data.mciDetails || []).map((d) => ({
    item_name: d.item_name, qty: d.raw_qty, rate: numberFormat(d.raw_item_rate), unit: d.unit_name, total: numberFormat(d.raw_item_total),
  }));

  const grandTotal = Number(data.total_cost || 0);

  const voucherDetailsLines = [
    `Date: ${dateReversed}`,
    data.acc_name ? `Account: ${data.acc_name}` : null,
    data.customer_po_ref ? `Customer PO Ref: ${data.customer_po_ref}` : null,
    data.payment_terms ? `Payment Terms: ${data.payment_terms} Days` : null,
  ].filter(Boolean);

  const producedByLines = [
    data.full_name ? `Voucher By: ${data.full_name}` : null,
    institution?.street_address ? `Address: ${institution.street_address}` : null,
    institution?.phone_no ? `Contact No: ${institution.phone_no}` : null,
    data.acc_mgr ? `${isPharma ? "Rep." : "Account Manager"}: ${data.acc_mgr}` : null,
    institution?.vat_license ? `${isUAE ? "TRN No" : "Tax Reg No"}: ${institution.vat_license}` : null,
    data.licence_no ? `License No: ${data.licence_no}` : null,
  ].filter(Boolean);

  const logo = logoUrl ? (
    <img src={logoUrl} alt={companyName} />
  ) : (
    <span className="da-logo-initials">{(companyName || "?").slice(0, 1)}</span>
  );

  return (
    <div className="da-root">
      <div className="da-header-band">
        <div className="da-header-left">
          <div className="da-logo-tile">{logo}</div>
          <p className="da-company-name">{companyName}</p>
        </div>
        <div className="da-header-right">
          <p className="da-doc-title">MANUFACTURING VOUCHER</p>
          <p className="da-doc-number">{data.mf_voucher_no}</p>
        </div>
      </div>

      <div style={{ padding: "0 48px" }}>
        <div className="da-party-block">
          <div className="da-party-col">
            <p className="da-label">Voucher Details</p>
            {voucherDetailsLines.map((l, i) => <div key={i} className="da-party-line">{l}</div>)}
          </div>
          <div className="da-party-col">
            <p className="da-label">Produced By</p>
            {producedByLines.map((l, i) => <div key={i} className="da-party-line">{l}</div>)}
          </div>
        </div>

        <ItemsTable title="Produced Items" rows={finishedRows} />
        <ItemsTable title="Consumed Items" rows={rawRows} />

        <div className="da-totals-area">
          <div className="da-totals-left">
            <p className="da-amount-words">In Words: {convertAmountToWordsDesignA(grandTotal, { country, currency })}</p>
            <div className="da-bank-details"><p className="da-label">Description</p><div>{data.narration || "-"}</div></div>
          </div>
          <div className="da-totals-right">
            <div className="da-totals-row"><span>Labour Cost</span><span className="da-numeric">{numberFormat(data.labour_cost || 0)}</span></div>
            <div className="da-totals-row"><span>Other Cost</span><span className="da-numeric">{numberFormat(data.others_cost || 0)}</span></div>
            <div className="da-grand-bar"><span>Grand Total ({currency || ""})</span><span className="da-numeric">{numberFormat(grandTotal)}</span></div>
          </div>
        </div>
      </div>

      <div className="da-footer-bar">
        <span>This is a computer-generated document.</span>
        <span></span>
      </div>
    </div>
  );
}
